import SecureInputFiles from '../components/Files/SecureInputFiles';
import { surveyorSupplementPath } from '../utils/surveyorSupplementPath';
import SurveyorAdditionalActions from '../components/Quality/SurveyorAdditionalActions';
import { hasAdditional } from '../components/Quality/additionalActionsUi';
import { canCompleteSurvey, canWorkSurvey, arrivalReady, isOwnSurvey } from '../dispatchUi';
import {
    Modal, ModalDialog, ModalOverflow, ModalClose,
    Box, Stack, Typography, Chip, Divider, IconButton, Link, Button,
    Sheet,
    Drawer,
    CircularProgress
} from "@mui/joy";
import { useContext, useEffect, useMemo, useState } from "react";
import { LayoutContext } from "../context";
import { AnimatePresence, motion } from "framer-motion";
import { APIGetFiles, getBitrixFileProxyUrl } from "../api";
import Cookies from 'js-cookie';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import SquareFootRoundedIcon from '@mui/icons-material/SquareFootRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import ContentCopyRoundedIcon  from '@mui/icons-material/ContentCopyRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import DirectionsCarRoundedIcon from '@mui/icons-material/DirectionsCarRounded';
import DesignServicesRoundedIcon from '@mui/icons-material/DesignServicesRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import DeadlineRoundedIcon from '@mui/icons-material/EventBusyRounded';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import DrawRoundedIcon from '@mui/icons-material/DrawRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import CameraAltRoundedIcon from '@mui/icons-material/CameraAltRounded';

import { useNavigate, useParams } from "react-router";
import AddedByChip from "../components/AddedByChip";



// ─── Конфиг статусов ─────────────────────────────────────────────────────────

const STATE_CONFIG = {
    completed:   { label: 'Завершено',     color: '#ffffff', bg: 'rgba(31,122,31,0.1)',  icon: <CheckCircleRoundedIcon sx={{ fontSize: 15 }} /> },
    in_progress: { label: 'В работе',      color: '#0f7bab', bg: 'rgba(15,123,171,0.1)', icon: <HourglassEmptyRoundedIcon sx={{ fontSize: 15 }} /> },
    new:         { label: 'Новое',         color: '#572AC3', bg: 'rgba(87,42,195,0.1)',  icon: <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 15 }} /> },
    planned:     { label: 'Запланировано', color: '#b07d00', bg: 'rgba(176,125,0,0.1)',  icon: <CalendarMonthRoundedIcon sx={{ fontSize: 15 }} /> },
    drawing:     { label: 'Чертёж',        color: '#0f7bab', bg: 'rgba(15,123,171,0.1)', icon: <DrawRoundedIcon sx={{ fontSize: 15 }} /> },
};

function StatusBadge({ state }) {
    const cfg = STATE_CONFIG[state] || STATE_CONFIG['new'];
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.6,
            px: 1.5, py: 0.5, borderRadius: '20px',
            background: 'rgba(255,255,255,0.2)',
            color: 'white', fontWeight: 700, fontSize: '0.75rem',
        }}>
            {cfg.icon}{cfg.label}
        </Box>
    );
}


function formatFileSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

function formatTime(t) {
    return t ? t.slice(0, 5) : null;
}

function buildTimeString(event) {
    const from = formatTime(event?.time_from);
    const to   = formatTime(event?.time_to);
    const main = formatTime(event?.time_event);
    if (from && to) return `${from} — ${to}`;
    if (main)       return main;
    return null;
}

function formatDate(dateStr) {
    if (!dateStr) return null;
    try {
        return new Date(dateStr).toLocaleDateString('ru-RU', {
            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
        });
    } catch { return dateStr; }
}


// ─── Строка с иконкой ─────────────────────────────────────────────────────────

function InfoRow({ icon, label, value, copyable = false, children }) {
    const display = children || value;
    if (!display && value !== 0) return null;

    return (
        <Box sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1.5,
            py: 1.1, px: 1.5, borderRadius: '12px', transition: 'background .15s',
            '&:hover': { background: 'rgba(87,42,195,0.04)' },
        }}>
            <Box sx={{
                mt: 0.1, width: 32, height: 32, borderRadius: '10px',
                background: 'rgba(87,42,195,0.08)', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                color: '#572AC3', flexShrink: 0,
            }}>
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.68rem', mb: 0.2 }}>
                    {label}
                </Typography>
                {children || (
                    <Typography level="body-sm" sx={{ fontWeight: 600, color: '#1a1a2e', wordBreak: 'break-word', lineHeight: 1.4 }}>
                        {value}
                    </Typography>
                )}
            </Box>
            {copyable && value && (
                <IconButton size="sm" variant="plain"
                    onClick={() => navigator.clipboard.writeText(value).catch(() => {})}
                    sx={{ color: 'text.tertiary', flexShrink: 0, mt: 0.1,
                        '&:hover': { color: '#572AC3', background: 'rgba(87,42,195,0.08)' } }}
                >
                    <ContentCopyRoundedIcon sx={{ fontSize: 14 }} />
                </IconButton>
            )}
        </Box>
    );
}


// ─── Блок-секция ──────────────────────────────────────────────────────────────

function Section({ title, icon, children, accent }) {
    const color = accent || '#572AC3';
    return (
        <Box sx={{ mx: 1, mt: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={0.7} sx={{ mb: 0.5, px: 1 }}>
                <Box sx={{ color, display: 'flex', fontSize: 16 }}>{icon}</Box>
                <Typography level="body-xs" sx={{ color, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {title}
                </Typography>
            </Stack>
            <Box sx={{
                borderRadius: '14px',
                border: `1.5px solid ${color}18`,
                background: `${color}05`,
                overflow: 'hidden',
            }}>
                {children}
            </Box>
        </Box>
    );
}


export default function SurveyorEventDetailPage() {
    const { account, listEvents, updateListEvents, tokenEmployee } = useContext(LayoutContext);
    const { bitrixId } = useParams();
    const navigate = useNavigate();
    const [files, setFiles] = useState([]);
    const [filesLoading, setFilesLoading] = useState(false);
    const [filesLink, setFilesLink] = useState(null);

    const close  = () => navigate(-1);

    // Пока список не догрузился, «не найдено» показывать нельзя.
    //
    // listEvents в контексте стартует пустым МАССИВОМ, поэтому проверка
    // Array.isArray ниже проходила всегда, и на первом же кадре — до ответа
    // сервера — обмерщику показывалось «Событие не найдено» на только что
    // назначенном событии. Отдельный флаг ждёт именно завершения загрузки.
    const [listLoading, setListLoading] = useState(true);

    useEffect(() => {
        if (!account?.id) return undefined;
        let alive = true;
        setListLoading(true);
        Promise.resolve(updateListEvents(null, account.id))
            .catch(() => {})
            .finally(() => { if (alive) setListLoading(false); });
        return () => { alive = false; };
    }, [account?.id]);

    // Ищем событие по bitrixId из URL
    const event = useMemo(() => {
        if (!bitrixId || !Array.isArray(listEvents)) return null;
        return listEvents.find(e => e?.bitrix_deal?.id?.toString() === bitrixId.toString());
    }, [bitrixId, listEvents]);

    console.log("bitrixId", event, bitrixId, listEvents);

    // Загружаем файлы сделки
    useEffect(() => {
        if (event?.bitrix_deal?.id) {
            setFiles([]);
            setFilesLoading(true);
            APIGetFiles(event.bitrix_deal.id).then(data => {
                if (data?.files) setFiles(data.files);
                if (data?.public_link) setFilesLink(data?.public_link);
                setFilesLoading(false);
            }).catch(() => setFilesLoading(false));
        }
    }, [event?.bitrix_deal?.id, bitrixId]);

    // Список ещё грузится — спиннер
    if (listLoading || !Array.isArray(listEvents)) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(180deg, #f5f3fc 0%, #ffffff 100%)' }}>
                <CircularProgress size="lg" />
            </Box>
        );
    }

    // Список загружен, но события нет (не назначено этому обмерщику / уже закрыто)
    if (!event) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', gap: 1.5,
                alignItems: 'center', justifyContent: 'center', p: 3, textAlign: 'center',
                background: 'linear-gradient(180deg, #f5f3fc 0%, #ffffff 100%)' }}>
                <WarningAmberRoundedIcon sx={{ fontSize: 40, color: '#b07d00' }} />
                <Typography level="title-md">Событие не найдено</Typography>
                <Typography level="body-sm" sx={{ color: 'text.tertiary', maxWidth: 320 }}>
                    Возможно, оно ещё не назначено вам или уже закрыто.
                </Typography>
                <Button variant="soft" startDecorator={<ArrowBackRoundedIcon />} onClick={() => navigate(-1)}>
                    Назад
                </Button>
            </Box>
        );
    }

    const deal     = event?.bitrix_deal;
    const city     = deal?.city?.name || null;
    const address  = deal?.address   || null;
    const fullAddress = [city, address].filter(Boolean).join(', ') || null;
    const timeStr  = buildTimeString(event);
    const dateStr  = formatDate(event?.date_event);
    const programs = deal?.program_for_draftsman?.map(p => p.name) ?? [];
    const additionalServices = event?.add_event_services ?? [];
    const questionnaire = event?.questionnaire;
    const managerName = event?.manager
        ? `${event.manager?.last_name || ''} ${event.manager.name || ''}`.trim()
        : null;

    return (
        <Box sx={{
            minHeight: '100dvh',
            background: 'linear-gradient(180deg, #f5f3fc 0%, #ffffff 100%)',
        }}>
            <Box sx={{ maxWidth: 600, mx: 'auto', px: { xs: 1.5, md: 3 } }}>

                {/* ── Шапка (скопировано из ModalEventSurveyor, без ModalClose) ── */}
                <Box sx={{
                    background: 'linear-gradient(135deg, #572AC3 0%, #8d6aec 100%)',
                    px: 3, pt: 3, pb: 2.5, borderRadius: '22px',
                    my: 2, boxShadow: '0 8px 32px rgba(87,42,195,0.18)',
                }}>
                    <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                        <IconButton
                            variant="plain"
                            onClick={() => navigate(-1)}
                            sx={{ 
                                borderRadius: '12px',
                                '&:hover': { background: 'rgba(255,255,255,0.15)' }
                            }}
                        >
                            <ArrowBackRoundedIcon sx={{ color: "white" }} />
                        </IconButton>
                        
                        <Stack direction="column" alignItems="flex-start" spacing={1.5} sx={{ mb: 1.5 }}>
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                                <Box sx={{
                                    width: 42, height: 42, borderRadius: '13px',
                                    background: 'rgba(255,255,255,0.2)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <AssignmentRoundedIcon sx={{ color: 'white', fontSize: 22 }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.72rem', fontWeight: 600 }}>
                                        Событие #{event?.bitrix_deal?.id}
                                        {event?.is_additional_measuring && ' · ⚠️ Доп. обмер'}
                                    </Typography>
                                    <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.2 }}>
                                        {event?.name_state || 'Замер помещения'}
                                    </Typography>
                                    {['cancelled', 'moved', 'postponed_to_date'].includes(event?.effective_state) && (
                                        <Chip
                                            size="sm"
                                            color={event.effective_state === 'cancelled' ? 'danger' : 'warning'}
                                            variant="soft"
                                            sx={{ ml: 1 }}
                                        >
                                            {event.effective_state === 'cancelled' ? 'Выезд отменён'
                                            : event.effective_state === 'moved' ? 'Выезд перенесён'
                                            : 'Перенесено на дату'}
                                        </Chip>
                                    )}
                                </Box>
                            </Stack>
                            <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.7}>
                                <StatusBadge state={event?.state} />
                                {event?.is_completed && (
                                    <Box sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.6,
                                        px: 1.5, py: 0.5, borderRadius: '20px',
                                        background: 'rgba(255,255,255,0.2)',
                                        color: 'white', fontWeight: 700, fontSize: '0.75rem',
                                    }}>
                                        ✓ Обмер выполнен
                                    </Box>
                                )}
                            </Stack>
                        </Stack>
                    </Stack>
                </Box>

                {/* ── Тело ── */}
                <Box sx={{ pb: 3 }}>

                    {/* Дата и время */}
                    <Box sx={{
                        mx: 2, mt: 2, p: 2, borderRadius: '14px',
                        background: 'linear-gradient(135deg, rgba(87,42,195,0.06), rgba(141,106,236,0.06))',
                        border: '1.5px solid rgba(87,42,195,0.1)',
                        display: 'flex', gap: 4, flexWrap: 'wrap',
                    }}>
                        {dateStr && (
                            <Box>
                                <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.68rem', mb: 0.3 }}>Дата</Typography>
                                <Stack direction="row" alignItems="center" spacing={0.7}>
                                    <CalendarMonthRoundedIcon sx={{ fontSize: 16, color: '#572AC3' }} />
                                    <Typography sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: '0.88rem', textTransform: 'capitalize' }}>
                                        {dateStr}
                                    </Typography>
                                </Stack>
                            </Box>
                        )}
                        {/* {timeStr && ( */}
                        <Box>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.68rem', mb: 0.3 }}>Время</Typography>
                            <Stack direction="row" alignItems="center" spacing={0.7}>
                                <AccessTimeRoundedIcon sx={{ fontSize: 16, color: '#572AC3' }} />
                                <Typography sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: '0.88rem' }}>
                                    {timeStr}
                                </Typography>
                            </Stack>
                        </Box>
                        {/* )} */}
                        {/* {event?.drawing_deadline && (
                            <Box>
                                <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.68rem', mb: 0.3 }}>Дедлайн чертежа</Typography>
                                <Stack direction="row" alignItems="center" spacing={0.7}>
                                    <DeadlineRoundedIcon sx={{ fontSize: 16, color: '#c30013' }} />
                                    <Typography sx={{ fontWeight: 700, color: '#c30013', fontSize: '0.88rem' }}>
                                        {formatDateTime(event.drawing_deadline)}
                                    </Typography>
                                </Stack>
                            </Box>
                        )} */}
                    </Box>


                    {/* ── Анкета обмерщика ── */}
                    {questionnaire && (
                        <Box sx={{
                            mx: 2, mt: 2, p: 2, borderRadius: '14px',
                            background: 'rgba(31,122,31,0.05)',
                            border: '1.5px solid rgba(31,122,31,0.2)',
                        }}>
                            <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 1.5 }}>
                                <CheckCircleRoundedIcon sx={{ fontSize: 17, color: '#1F7A1F' }} />
                                <Typography sx={{ fontWeight: 700, color: '#1F7A1F', fontSize: '0.85rem' }}>
                                    Событие выполнено
                                </Typography>
                            </Stack>
                            <Stack spacing={1}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Выезд состоялся:</Typography>
                                    <Chip size="sm"
                                        sx={{
                                            background: questionnaire.status === 'yes' ? 'rgba(31,122,31,0.12)' : 'rgba(195,0,19,0.1)',
                                            color: questionnaire.status === 'yes' ? '#1F7A1F' : '#c30013',
                                            fontWeight: 700, border: 'none',
                                        }}
                                    >
                                        {questionnaire.status_text}
                                    </Chip>
                                </Stack>
                                {questionnaire.comment && (
                                    <Box>
                                        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.68rem', mb: 0.2 }}>Комментарий</Typography>
                                        <Typography level="body-sm" sx={{ fontWeight: 600, color: '#1a1a2e' }}>{questionnaire.comment}</Typography>
                                    </Box>
                                )}
                                {questionnaire.link_media_file && (
                                    <Box>
                                        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.68rem', mb: 0.2 }}>Ссылка от замерщика</Typography>
                                        <Link href={questionnaire.link_media_file} target="_blank" rel="noopener noreferrer"
                                            sx={{ fontSize: '0.82rem', fontWeight: 600, wordBreak: 'break-word' }}
                                        >
                                            {questionnaire.link_media_file}
                                        </Link>
                                    </Box>
                                )}
                                {questionnaire.reason_for_cancelation && (
                                    <Box>
                                        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.68rem', mb: 0.2 }}>Причина отмены</Typography>
                                        <Typography level="body-sm" sx={{ fontWeight: 600, color: '#c30013' }}>{questionnaire.reason_for_cancelation}</Typography>
                                    </Box>
                                )}
                                {questionnaire.transfer_reason && (
                                    <Box>
                                        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.68rem', mb: 0.2 }}>Причина переноса</Typography>
                                        <Typography level="body-sm" sx={{ fontWeight: 600, color: '#1a1a2e' }}>{questionnaire.transfer_reason}</Typography>
                                    </Box>
                                )}
                                {questionnaire.transfer_date_replacement && (
                                    <Box>
                                        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.68rem', mb: 0.2 }}>Дата переноса</Typography>
                                        <Typography level="body-sm" sx={{ fontWeight: 600, color: '#1a1a2e' }}>
                                            {new Date(questionnaire.transfer_date_replacement).toLocaleDateString('ru-RU')}
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                    )}


                    {/* ── Место и контакт ── */}
                    <Section title="Адрес и контакт" icon={<LocationOnRoundedIcon sx={{ fontSize: 16 }} />}>
                        <InfoRow icon={<LocationOnRoundedIcon sx={{ fontSize: 16 }} />} label="Адрес" value={fullAddress} copyable />
                        <InfoRow icon={<PhoneRoundedIcon sx={{ fontSize: 16 }} />} label="Контактный телефон" value={deal?.contact_phone} copyable />
                    </Section>


                    {/* ── Параметры сделки ── */}
                    <Section title="Параметры объекта" icon={<SquareFootRoundedIcon sx={{ fontSize: 16 }} />}>
                        <InfoRow icon={<SquareFootRoundedIcon sx={{ fontSize: 16 }} />} label="Площадь" value={deal?.square ? `${deal.square} м²` : null} />
                        <InfoRow icon={<DirectionsCarRoundedIcon sx={{ fontSize: 16 }} />} label="Километраж на выезд" value={event?.transfer_km != null ? `${event.transfer_km} км` : null} />
                        <InfoRow icon={<PaidRoundedIcon sx={{ fontSize: 16 }} />} label="З/П Обмерщика" value={deal?.surveyor_salary ? `${deal.surveyor_salary} ₽` : null} />
                        <InfoRow icon={<PaidRoundedIcon sx={{ fontSize: 16 }} />} label="Сумма за выезд" value={event?.transfer_amount != null ? `${Number(event.transfer_amount).toLocaleString('ru-RU')} ₽` : null} />
                    </Section>

                    {/* ── Файлы ТЗ — всегда видны, со скелетоном при загрузке ── */}
                    <Section
                        title="Исходные данные и ТЗ"
                        icon={<InsertDriveFileRoundedIcon sx={{ fontSize: 16 }} />}
                    >
<SecureInputFiles files={files} folderUrl={filesLink} loading={filesLoading} />
</Section>
                    
                    {/* ── Участники ── */}
                    <Section title="Участники" icon={<PersonRoundedIcon sx={{ fontSize: 16 }} />}>
                        <InfoRow icon={<PersonRoundedIcon sx={{ fontSize: 16 }} />} label="Менеджер" value={managerName} />
                    </Section>

                    {/* ── Доп. услуги ── */}
                    {additionalServices.length > 0 && (
                        <Section title="Дополнительные услуги" icon={<FormatListBulletedRoundedIcon sx={{ fontSize: 16 }} />}>
                            <Box sx={{ px: 2, py: 1.5 }}>
                                <Stack spacing={0.8}>
                                    {additionalServices.map((srv, i) => {
                                        const title = srv.additional_service?.title || srv.custom_title || srv.title || '—';
                                        const survPrc = srv.custom_surveyor_price ?? srv.additional_service?.surveyor_price;
                                        const ct = srv.calculation_type || srv.additional_service?.calculation_type 
                                            || ((srv.additional_service?.full_price_volume ?? srv.full_price_volume) ? 'fixed' : 'per_sqm');
                                        const inputLabel = srv.input_label || srv.additional_service?.input_label;
                                        const inputUnit = srv.input_unit || srv.additional_service?.input_unit;
                                        const inputValue = srv.input_value;
                                        
                                        let priceLabel;
                                        if (ct === 'fixed')    priceLabel = `${survPrc} ₽ · фикс`;
                                        else if (ct === 'percent') priceLabel = `+${survPrc}%`;
                                        else if (ct === 'per_input') {
                                            priceLabel = inputValue
                                                ? `${inputValue} ${inputUnit || 'ед.'} × ${survPrc} ₽ = ${(inputValue * survPrc).toFixed(0)} ₽`
                                                : `${survPrc} ₽/${inputUnit || 'ед.'} · ${inputLabel || 'по вводу'}`;
                                        }
                                        else priceLabel = `${survPrc} ₽/м²`;
                                        
                                        return (
                                            <Box key={i} sx={{
                                                p: 1.2, borderRadius: '10px',
                                                background: 'rgba(87,42,195,0.05)',
                                                border: '1px solid rgba(87,42,195,0.1)',
                                            }}>
                                                <Typography level="body-sm" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.3 }}>
                                                    {title}
                                                </Typography>
                                                <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                                                    {survPrc != null && (
                                                        <Chip size="sm" sx={{ background: 'rgba(31,122,31,0.1)', color: '#1F7A1F', fontWeight: 600, border: 'none' }}>
                                                            {priceLabel}
                                                        </Chip>
                                                    )}
                                                    {ct === 'per_input' && !inputValue && (
                                                        <Chip size="sm" color="warning" variant="soft">
                                                            Чертёжник заполнит при сдаче
                                                        </Chip>
                                                    )}
                                                    <AddedByChip item={srv} />
                                                </Stack>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        </Section>
                    )}


                    {/* ── ТЗ ── */}
                    {deal?.tz && (
                        <Section title="Техническое задание" icon={<AssignmentRoundedIcon sx={{ fontSize: 16 }} />}>
                            <Box sx={{ px: 2, py: 1.5 }}>
                                <Typography level="body-sm" sx={{ color: '#1a1a2e', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                                    {deal.tz}
                                </Typography>
                            </Box>
                        </Section>
                    )}


                    {/* ── Комментарий ── */}
                    {event?.comment && (
                        <Section title="Комментарий" icon={<WarningAmberRoundedIcon sx={{ fontSize: 16 }} />} accent="#b07d00">
                            <Box sx={{ px: 2, py: 1.5 }}>
                                <Typography level="body-sm" sx={{ color: '#1a1a2e', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                                    {event.comment}
                                </Typography>
                            </Box>
                        </Section>
                    )}

                    {/* ── Комментарий менеджера обмерщику ── */}
                    {event?.manager_comment && (
                        <Section title="Комментарий от менеджера" icon={<WarningAmberRoundedIcon sx={{ fontSize: 16 }} />} accent="#c30013">
                            <Box sx={{ px: 2, py: 1.5 }}>
                                <Typography level="body-sm" sx={{ color: '#1a1a2e', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                                    {event.manager_comment}
                                </Typography>
                            </Box>
                        </Section>
                    )}

                    {event?.employee_comment && (
                        <Section title="Комментарий обмерщика" icon={<AssignmentRoundedIcon sx={{ fontSize: 16 }} />} accent="#0f7bab">
                            <Box sx={{ px: 2, py: 1.5 }}>
                                <Typography level="body-sm" sx={{ color: '#1a1a2e', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                                    {event.employee_comment}
                                </Typography>
                            </Box>
                        </Section>
                    )}


                    {/* ── Ссылка клиента ── */}
                    {event?.client_link?.link && (
                        <Section title="Ссылка клиента" icon={<LinkRoundedIcon sx={{ fontSize: 16 }} />}>
                            <Box sx={{ px: 2, py: 1.5 }}>
                                <Link href={event.client_link.link} target="_blank" rel="noopener noreferrer"
                                    sx={{ fontSize: '0.82rem', fontWeight: 600, wordBreak: 'break-all' }}
                                >
                                    {event.client_link.link}
                                </Link>
                            </Box>
                        </Section>
                    )}
                </Box>
            </Box>

            {/* ── Кнопки действий (sticky к низу) ── */}
            <Box sx={{
                position: 'sticky',
                bottom: 0, left: 0, right: 0,
                zIndex: 100,
                background: 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(16px)',
                borderTop: '1px solid rgba(87,42,195,0.1)',
                px: 2, py: 1.5,
                boxShadow: '0 -4px 20px rgba(0,0,0,0.06)',
            }}>
                <Stack direction="row" spacing={1} justifyContent="center"
                    sx={{ maxWidth: 600, mx: 'auto' }}
                >
                    <SurveyorAdditionalActions event={event} />
                    {/* Кнопка «Я на объекте» → страница с камерой. Пока не отмечено — выделяем. */}
                    {canWorkSurvey(event, account) && !event?.arrival?.skipped && (
                        arrivalReady(event) ? (
                            <Button
                                size="md"
                                variant="soft"
                                color="success"
                                disabled
                                startDecorator={<TaskAltRoundedIcon />}
                                sx={{ borderRadius: 12 }}
                            >
                                {event?.arrival?.skipped ? 'Прибытие пропущено менеджером' : 'Вы на объекте'}
                            </Button>
                        ) : (
                            <Button
                                size="md"
                                variant="solid"
                                color="primary"
                                onClick={() => navigate(`/surveyor/arrive/${event?.id}`)}
                                startDecorator={<CameraAltRoundedIcon />}
                                sx={{
                                    borderRadius: 12,
                                    fontWeight: 800,
                                    boxShadow: 'lg',
                                    animation: 'arrivePulse 1.6s infinite',
                                    '@keyframes arrivePulse': {
                                        '0%':   { boxShadow: '0 0 0 0 rgba(87,42,195,0.45)' },
                                        '70%':  { boxShadow: '0 0 0 12px rgba(87,42,195,0)' },
                                        '100%': { boxShadow: '0 0 0 0 rgba(87,42,195,0)' },
                                    },
                                    '&:hover': { transform: 'scale(1.02)' },
                                    transition: 'all 0.2s',
                                }}
                            >
                                Я на объекте
                            </Button>
                        )
                    )}

                    {/* Кнопка завершить → переход на страницу закрытия */}
                    <Button
                        size="md"
                        fullWidth
                        color="success"
                        disabled={hasAdditional(event) || !canCompleteSurvey(event, account)}
                        onClick={() => navigate(`/surveyor/close/${event?.bitrix_deal?.id}`)}
                        startDecorator={<TaskAltRoundedIcon />}
                        sx={{
                            borderRadius: 12,
                            background: (event?.state === 'completed' || event?.is_completed)
                                ? undefined
                                : 'success',
                            '&:not(:disabled):hover': { transform: 'scale(1.01)' },
                            transition: 'all 0.2s',
                        }}
                    >
                        {(event?.state === 'completed' || event?.is_completed)
                            ? 'Завершено'
                            : 'Завершить'
                        }
                    </Button>

                    {!hasAdditional(event) && canWorkSurvey(event, account) && (
                        <Button
                            size="md"
                            variant="soft"
                            color="danger"
                            onClick={() => navigate(`/surveyor/fail/${event?.bitrix_deal?.id}`)}
                            startDecorator={<CancelRoundedIcon />}
                            sx={{
                                borderRadius: 12,
                                backgroundColor: 'danger.700',
                                color: 'white',
                                '&:hover': { background: 'danger.600' },
                                transition: 'all 0.2s',
                            }}
                        >
                            Неудачное
                        </Button>
                    )}

                    {(!hasAdditional(event) && event?.is_completed && ['drawing', 'completed'].includes(event?.state)) && (
                        <Button
                            size="md"
                            variant="solid"
                            color="warning"
                            onClick={() => navigate(surveyorSupplementPath(event))}
                            startDecorator={<AddPhotoAlternateRoundedIcon />}
                            sx={{ borderRadius: 12, transition: 'all 0.2s' }}
                        >
                            Дополнить
                        </Button>
                    )}
                </Stack>
            </Box>
        </Box>
    );
}